import BaseController from "confinus_ui/base_controller";
import http from "lib/http";

interface UomClassification {
  measurable: string[];
  countable: string[];
}

export default class extends BaseController {
  static targets = [
    "sellUomSelect",
    "bridgeUomSelect",
    "weightBridgeFields",
    "countBridgeFields",
    "catchWeightToggle",
    "sizeFields",
    "partFields",
    "extraChargeFields",
    "portionFields",
    "portionUomWarning",
    "uomWarning",
  ];

  static values = {
    priceUom: { type: String, default: "" },
    validateUrl: { type: String, default: "" },
  };

  declare readonly hasSellUomSelectTarget: boolean;
  declare readonly sellUomSelectTarget: HTMLSelectElement;
  declare readonly hasBridgeUomSelectTarget: boolean;
  declare readonly bridgeUomSelectTarget: HTMLSelectElement;
  declare readonly hasWeightBridgeFieldsTarget: boolean;
  declare readonly weightBridgeFieldsTarget: HTMLElement;
  declare readonly hasCountBridgeFieldsTarget: boolean;
  declare readonly countBridgeFieldsTarget: HTMLElement;
  declare readonly hasCatchWeightToggleTarget: boolean;
  declare readonly catchWeightToggleTarget: HTMLElement;
  declare readonly hasSizeFieldsTarget: boolean;
  declare readonly sizeFieldsTarget: HTMLElement;
  declare readonly hasPartFieldsTarget: boolean;
  declare readonly partFieldsTarget: HTMLElement;
  declare readonly hasExtraChargeFieldsTarget: boolean;
  declare readonly extraChargeFieldsTarget: HTMLElement;
  declare readonly hasPortionFieldsTarget: boolean;
  declare readonly portionFieldsTarget: HTMLElement;
  declare readonly hasPortionUomWarningTarget: boolean;
  declare readonly portionUomWarningTarget: HTMLElement;
  declare readonly hasUomWarningTarget: boolean;
  declare readonly uomWarningTarget: HTMLElement;

  declare priceUomValue: string;
  declare validateUrlValue: string;

  private uomClassification: UomClassification = {
    measurable: [],
    countable: [],
  };
  private validateTimeout: ReturnType<typeof setTimeout> | null = null;

  connect() {
    this.loadUomClassification();
    this.updateConditionalFields();
  }

  // Resolve the actual <select> element from a target that may be a ConfinusUI wrapper
  private resolveSelect(
    target: HTMLSelectElement | HTMLElement,
  ): HTMLSelectElement | null {
    if (target.tagName === "SELECT") return target as HTMLSelectElement;
    return target.querySelector("select");
  }

  disconnect() {
    if (this.validateTimeout) {
      clearTimeout(this.validateTimeout);
    }
  }

  // Stimulus callback: fired when priceUomValue changes via data attribute
  priceUomValueChanged() {
    this.updateConditionalFields();
  }

  // Called when sell UoM selection changes
  sellUomChanged() {
    this.updateConditionalFields();
    this.validateUomCombo();
  }

  // Called when bridge UoM (weight/count) selection changes
  bridgeUomChanged() {
    this.validateUomCombo();
  }

  // Called when user toggles the portioning switch
  togglePortioning(event: Event) {
    const checkbox = event.target as HTMLInputElement;
    if (this.hasPortionFieldsTarget) {
      this.portionFieldsTarget.classList.toggle("hidden", !checkbox.checked);
      // Clear portion fields when unchecked so they don't submit
      if (!checkbox.checked) {
        this.portionFieldsTarget
          .querySelectorAll("input, select")
          .forEach((el) => {
            const input = el as HTMLInputElement | HTMLSelectElement;
            if (input.type === "checkbox") {
              (input as HTMLInputElement).checked = false;
            } else {
              input.value = "";
            }
          });
      }
    }
  }

  // Called when user toggles the extra charge switch
  toggleExtraCharge(event: Event) {
    const checkbox = event.target as HTMLInputElement;
    if (this.hasExtraChargeFieldsTarget) {
      this.extraChargeFieldsTarget.classList.toggle(
        "hidden",
        !checkbox.checked,
      );
      if (!checkbox.checked) {
        this.extraChargeFieldsTarget
          .querySelectorAll("input, select")
          .forEach((el) => {
            (el as HTMLInputElement | HTMLSelectElement).value = "";
          });
      }
    }
  }

  // Called when portion UoM selection changes — validate against price UoM
  portionUomChanged() {
    this.validatePortionUom();
  }

  // Private

  private validatePortionUom() {
    if (!this.hasPortionUomWarningTarget) return;

    const portionUomSelect = this.element.querySelector(
      'select[name*="[sell_size_uom]"]',
    ) as HTMLSelectElement | null;
    const portionUom = portionUomSelect?.value;
    const priceUom = this.priceUomValue;

    if (!portionUom || !priceUom) {
      this.portionUomWarningTarget.classList.add("hidden");
      this.portionUomWarningTarget.textContent = "";
      return;
    }

    // Use the same validate endpoint
    http
      .get<{ valid: boolean; error?: string }>(this.validateUrlValue, {
        params: { price_uom: priceUom, sell_uom: portionUom },
      })
      .then((response) => {
        const data = response.data;
        if (data.valid) {
          this.portionUomWarningTarget.classList.add("hidden");
          this.portionUomWarningTarget.textContent = "";
        } else {
          this.portionUomWarningTarget.textContent = `Portion UoM incompatible: ${data.error || "cannot convert to price UoM"}`;
          this.portionUomWarningTarget.classList.remove("hidden");
        }
      })
      .catch(() => {
        this.portionUomWarningTarget.classList.add("hidden");
      });
  }

  private validateUomCombo() {
    if (this.validateTimeout) clearTimeout(this.validateTimeout);
    this.validateTimeout = setTimeout(() => this.executeUomValidation(), 300);
  }

  private async executeUomValidation() {
    if (!this.validateUrlValue || !this.hasSellUomSelectTarget) return;

    const sellUom = this.resolveSelect(this.sellUomSelectTarget)?.value || "";
    const priceUom = this.priceUomValue;
    if (!sellUom || !priceUom) return;

    const bridgeUom = this.hasBridgeUomSelectTarget
      ? this.resolveSelect(this.bridgeUomSelectTarget)?.value || ""
      : "";

    try {
      const params = new URLSearchParams({
        price_uom: priceUom,
        sell_uom: sellUom,
      });
      if (bridgeUom) params.set("bridge_uom", bridgeUom);

      const response = await http.get<{
        valid: boolean;
        direct?: boolean;
        error?: string;
      }>(`${this.validateUrlValue}?${params.toString()}`);
      const data = response.data;

      if (data.valid) {
        this.clearWarning();
        if (data.direct) {
          // Direct relationship - no bridge needed, could clear bridge fields
        }
      } else {
        this.showWarning(data.error ?? "Invalid UoM combination");
      }
    } catch {
      // Network error - don't block the user, server-side validation will catch it
    }
  }

  private loadUomClassification() {
    const wizardEl = this.element.closest(
      "[data-controller*='product-wizard']",
    );
    const dataEl = wizardEl?.querySelector(
      "[data-platform-admin--product-wizard-target='uomData']",
    );
    if (dataEl?.textContent) {
      try {
        this.uomClassification = JSON.parse(dataEl.textContent);
      } catch {
        // keep defaults
      }
    }
  }

  private updateConditionalFields() {
    if (!this.hasSellUomSelectTarget) return;

    const sellUom = this.resolveSelect(this.sellUomSelectTarget)?.value || "";
    const priceUom = this.priceUomValue;

    if (!sellUom || !priceUom) {
      this.hideAllBridgeFields();
      this.clearWarning();
      return;
    }

    const sellIsMeasurable = this.isMeasurable(sellUom);
    const sellIsCountable = this.isCountable(sellUom);
    const priceIsMeasurable = this.isMeasurable(priceUom);
    const priceIsCountable = this.isCountable(priceUom);

    // Reset
    this.hideAllBridgeFields();
    this.clearWarning();

    if (sellIsMeasurable) {
      if (priceIsMeasurable) {
        this.showTarget("catchWeightToggle");
      } else {
        this.showWarning(
          "A measurable sell UoM with a countable price UoM is unusual. Consider changing the price UoM.",
        );
      }
    } else if (sellIsCountable) {
      if (priceIsMeasurable) {
        this.showTarget("weightBridgeFields");
        this.showTarget("catchWeightToggle");
      } else if (priceIsCountable) {
        if (sellUom !== priceUom) {
          this.showTarget("countBridgeFields");
        }
      }

      this.showTarget("partFields");
    }

    // Also run server-side validation for dimensional compatibility
    this.validateUomCombo();
  }

  private hideAllBridgeFields() {
    this.hideTarget("weightBridgeFields");
    this.hideTarget("countBridgeFields");
    this.hideTarget("catchWeightToggle");
    this.hideTarget("partFields");
  }

  private showTarget(name: string) {
    const hasTarget = `has${name.charAt(0).toUpperCase() + name.slice(1)}Target`;
    if ((this as any)[hasTarget]) {
      (this as any)[`${name}Target`].classList.remove("hidden");
    }
  }

  private hideTarget(name: string) {
    const hasTarget = `has${name.charAt(0).toUpperCase() + name.slice(1)}Target`;
    if ((this as any)[hasTarget]) {
      (this as any)[`${name}Target`].classList.add("hidden");
    }
  }

  private showWarning(message: string) {
    if (this.hasUomWarningTarget) {
      this.uomWarningTarget.textContent = message;
      this.uomWarningTarget.classList.remove("hidden");
    }
  }

  private clearWarning() {
    if (this.hasUomWarningTarget) {
      this.uomWarningTarget.textContent = "";
      this.uomWarningTarget.classList.add("hidden");
    }
  }

  private isMeasurable(uom: string): boolean {
    return this.uomClassification.measurable.includes(uom);
  }

  private isCountable(uom: string): boolean {
    return this.uomClassification.countable.includes(uom);
  }
}
